/**
 * Como é a mesa (docs/spec/explore-rooms.md, "Como é a mesa"): os nomes das línguas e as opções dos
 * campos, para o Perfil da mesa, a criação, a visão geral, os cartões e os filtros do Explorar.
 */
import type { Language } from '../i18n/index.js';
import { ROOM_FREQUENCIES, ROOM_LANGUAGES, ROOM_VISUALS, type RoomDetails, type RoomLanguage } from '../shared.js';

type T = (key: string, params?: Record<string, string | number>) => string;
type Option<V extends string> = { value: V; label: string };

/** O nome da língua na língua da interface ("Português", "Inglês"), com a inicial maiúscula. */
export function languageName(code: string, ui: Language): string {
  try {
    const name = new Intl.DisplayNames([ui], { type: 'language' }).of(code);
    if (name && name !== code) return name.charAt(0).toLocaleUpperCase(ui) + name.slice(1);
  } catch {
    // sem Intl.DisplayNames ou código estranho: fica o código
  }
  return code.toUpperCase();
}

/** A língua da interface como língua da mesa: a mesa nova já começa nela. */
export function roomLanguageOf(ui: Language): RoomLanguage {
  return ui === 'pt-BR' ? 'pt' : 'en';
}

/** As línguas da lista em ordem alfabética do nome, com "Não informado" no alto. */
export function languageOptions(t: T, ui: Language): Option<RoomDetails['language']>[] {
  const names = ROOM_LANGUAGES.map((code) => ({ value: code, label: languageName(code, ui) })).sort((a, b) => a.label.localeCompare(b.label, ui));
  return [{ value: '', label: t('roomDetails.none') }, ...names];
}

export function frequencyOptions(t: T): Option<RoomDetails['frequency']>[] {
  return [{ value: '', label: t('roomDetails.none') }, ...ROOM_FREQUENCIES.map((id) => ({ value: id, label: t(`roomDetails.frequency.${id}`) }))];
}

export function visualsOptions(t: T): Option<RoomDetails['visuals']>[] {
  return [{ value: '', label: t('roomDetails.none') }, ...ROOM_VISUALS.map((id) => ({ value: id, label: t(`roomDetails.visuals.${id}`) }))];
}

/** "Gratuita", "Paga" ou "Paga · R$ 20 por sessão". */
export function payLabel(details: Pick<RoomDetails, 'payToPlay' | 'price'>, t: T): string {
  if (!details.payToPlay) return t('roomDetails.free');
  return details.price ? t('roomDetails.paidWithPrice', { price: details.price }) : t('roomDetails.paid');
}
